<script setup lang="ts">
useSeoMeta({
  title: 'QikSolve solutions and capability',
  description:
    'Explore how QikSolve helps teams assess fit and choose a practical quality pathway for controlled digital operations.',
  ogTitle: 'QikSolve solutions and capability',
  ogDescription:
    'Explore how QikSolve helps teams assess fit and choose a practical quality pathway for controlled digital operations.',
  ogType: 'website',
  ogImage: 'https://www.qiksolve.com/images/qiksolve_cropped-logo.png',
  ogUrl: 'https://www.qiksolve.com/solutions-and-capability/',
  twitterCard: 'summary_large_image',
  twitterTitle: 'QikSolve solutions and capability',
  twitterDescription:
    'Explore how QikSolve helps teams assess fit and choose a practical quality pathway for controlled digital operations.',
  twitterImage: 'https://www.qiksolve.com/images/qiksolve_cropped-logo.png',
});
</script>

<template>
  <div class="bg-slate-50">
    <ArchetypeHero
      eyebrow="Solutions and capability"
      title="QikSolve solutions and capability"
      summary="Generic systems can force teams to compromise the way they work. QikSolve helps you understand the real quality or operational question, assess fit, and choose a proportionate path from current practice to controlled digital operations."
      :cta="{ label: 'Start a conversation', to: '/contact' }"
    />

    <div class="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
      <ArchetypeSection
        title="Start with the work, not a generic system"
        content="A useful solution needs to reflect the process it supports, the people who own it, the records it creates, and the evidence needed to make sound decisions. QikSolve starts with that context before recommending a product, service, or implementation pathway."
      />

      <ArchetypeSteps
        title="A proportionate path to a workable solution"
        :items="[
          { title: 'Frame the question', description: 'Identify the quality or operational problem that needs a clearer, more controlled way of working.' },
          { title: 'Understand the context', description: 'Map current practice, ownership, records, decisions, controls, evidence, and available capacity.' },
          { title: 'Assess fit', description: 'Consider the requirements and constraints that determine whether a corporate, service, or product pathway is appropriate.' },
          { title: 'Build capability', description: 'Implement a workable path, support adoption, and improve the operating model as evidence and experience develop.' },
        ]"
      />

      <section class="mt-12 grid gap-6 lg:grid-cols-2">
        <ArchetypeSection
          title="What fit should account for"
          :items="[
            { title: 'Process reality', description: 'The solution should reflect how work is actually performed, reviewed, and improved.' },
            { title: 'Ownership and decisions', description: 'Responsibilities, approvals, and decision points should remain clear to the people accountable for the work.' },
            { title: 'Records and evidence', description: 'The resulting practice should support traceability, validation evidence, and controlled change where required.' },
            { title: 'Capacity to sustain it', description: 'The path should be proportionate to intended use, risk, requirements, existing practice, and team capacity.' },
          ]"
          compact
        />

        <ArchetypeSection
          title="Where QikSolve can help"
          :items="[
            { title: 'Practical service pathways', description: 'Connect process understanding, implementation, assurance, adoption, maintenance, and continual improvement.' },
            { title: 'Product pathway orientation', description: 'Use the corporate context to identify when the detailed product answer belongs with the relevant product site.' },
            { title: 'Human accountability', description: 'Keep accountable people involved in decisions, review, evidence, and controlled change.' },
            { title: 'A sensible next step', description: 'Begin with the smallest meaningful step that the organisation can understand and sustain.' },
          ]"
          compact
        />
      </section>

      <nav
        aria-label="Related QikSolve pathways"
        class="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <NuxtLink
          to="/about"
          class="rounded-2xl border border-slate-200 bg-white p-6 shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-card-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        >
          <span class="font-semibold text-brand-900">About QikSolve</span>
          <span class="mt-2 block text-sm leading-6 text-slate-600">Understand the practitioner-led approach and human accountability.</span>
        </NuxtLink>
        <NuxtLink
          to="/services"
          class="rounded-2xl border border-slate-200 bg-white p-6 shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-card-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        >
          <span class="font-semibold text-brand-900">Services</span>
          <span class="mt-2 block text-sm leading-6 text-slate-600">See how implementation, assurance, adoption, and improvement can connect.</span>
        </NuxtLink>
        <NuxtLink
          to="/products"
          class="rounded-2xl border border-slate-200 bg-white p-6 shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-card-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        >
          <span class="font-semibold text-brand-900">Products</span>
          <span class="mt-2 block text-sm leading-6 text-slate-600">Orient yourself to the product pathways before exploring product detail.</span>
        </NuxtLink>
        <NuxtLink
          to="/solutions/excel-register-to-sharepoint"
          class="rounded-2xl border border-slate-200 bg-white p-6 shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-card-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        >
          <span class="font-semibold text-brand-900">Excel register migration</span>
          <span class="mt-2 block text-sm leading-6 text-slate-600">Explore SharePoint migration with a customer-reviewable validation sign-off pack.</span>
        </NuxtLink>
      </nav>

      <nav
        aria-label="Explore related needs"
        class="mt-8 flex flex-wrap gap-x-6 gap-y-3 border-t border-slate-200 pt-5 text-sm"
      >
        <NuxtLink
          to="/explore-by-need/choose-a-proportionate-path-to-workable-capability"
          class="font-semibold text-brand-700 underline decoration-brand-300 underline-offset-4 hover:text-brand-900"
        >
          Assess which QikSolve pathway fits
        </NuxtLink>
        <NuxtLink
          to="/explore-by-need/software-implementation-maintenance-and-management"
          class="font-semibold text-brand-700 underline decoration-brand-300 underline-offset-4 hover:text-brand-900"
        >
          Assess ongoing software implementation and support needs
        </NuxtLink>
      </nav>

      <section
        aria-labelledby="product-pathways-title"
        class="mt-12 grid gap-6 sm:grid-cols-2"
      >
        <h2
          id="product-pathways-title"
          class="sr-only"
        >
          Product pathways
        </h2>
        <a
          href="https://inq.qiksolve.com"
          class="rounded-2xl border border-slate-200 bg-white p-6 shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-card-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        >
          <span class="font-semibold text-brand-900">Explore INQ</span>
          <span class="mt-2 block text-sm leading-6 text-slate-600">Explore the INQ quality-operations pathway on its product site.</span>
        </a>
        <a
          href="https://qx.qiksolve.com"
          class="rounded-2xl border border-slate-200 bg-white p-6 shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-card-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        >
          <span class="font-semibold text-brand-900">Explore Qx</span>
          <span class="mt-2 block text-sm leading-6 text-slate-600">Explore the Qx governed record-intelligence pathway on its product site.</span>
        </a>
      </section>

      <ArchetypeCta
        class="mt-12"
        title="Have a quality or operational question to work through?"
        content="Bring the context, constraints, and evidence you already have. QikSolve can help identify a sensible next step without assuming a generic system is the answer."
        label="Contact QikSolve"
        to="/contact"
      />
    </div>
  </div>
</template>